import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import {
  getOpsClass,
  getOpsClasses,
  getOpsRegistrations,
  searchOpsContacts,
  setOpsRegistrationStatus,
} from '../../../requests';
import {
  createOpsRegistrationEx,
  getOpsEvalLink,
  getOpsProgramsForAccount,
  promoteOpsRegistration,
  transferOpsRegistration,
} from '../../../requests/opsRegistrationsExtra';
import {
  apiMessage,
  applyOpsRosterBatch,
  BATCH_ACTIONS,
  parseOpsRosterCsv,
  TBatchAction,
  TBatchParsedRow,
  TBatchResult,
} from '../../../requests/opsSlots';
import {
  btnGhost,
  btnPrimary,
  btnSecondary,
  Card,
  EmptyState,
  inputClass,
  labelClass,
  OriginBadge,
  PageBody,
  PageHeader,
  Pagination,
  Pill,
  tableClass,
  tdClass,
  thClass,
  theadClass,
  Toolbar,
  SortTh,
  useListSort,
} from '../ui';

const STATUSES = [
  'Booked',
  'Attended',
  'Completed',
  'No Attend',
  'Cancelled',
  'Transferred',
  'Waiting List',
  'Rescheduled',
];

const statusSelectClass =
  'rounded-md border border-white-two bg-white px-2 py-1 text-xs text-gray-five focus:border-blue-primary focus:outline-none focus:ring-2 focus:ring-blue-100';

const OpsRegistrations: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const classId = searchParams.get('classId') ?? '';
  const rosterMode = Boolean(classId);
  // From a person's page: only their registrations.
  const urlContactId = searchParams.get('contactId') ?? '';

  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [groupByCourse, setGroupByCourse] = useState(false);
  const { sortKey, sortDir, toggleSort } = useListSort();
  const [addOpen, setAddOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);

  // Add-person form
  const [contactQuery, setContactQuery] = useState('');
  const [contactId, setContactId] = useState('');
  const [contactLabel, setContactLabel] = useState('');
  const [newFirst, setNewFirst] = useState('');
  const [newLast, setNewLast] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [asWaitlist, setAsWaitlist] = useState(false);
  const [chargeProgramId, setChargeProgramId] = useState('');

  // Transfer panel: which row is open, and the class search inside it
  const [transferFor, setTransferFor] = useState('');
  const [transferQuery, setTransferQuery] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data: cls } = useQuery({
    queryKey: ['opsClass', classId],
    queryFn: () => getOpsClass(classId),
    enabled: rosterMode,
  });

  const { data, isLoading } = useQuery({
    queryKey: [
      'opsRegistrations',
      q,
      status,
      category,
      classId,
      sortKey,
      sortDir,
      urlContactId,
      from,
      to,
      page,
    ],
    queryFn: () =>
      getOpsRegistrations({
        from: from || undefined,
        to: to || undefined,
        contactId: urlContactId || undefined,
        sort: sortKey || undefined,
        dir: sortKey ? sortDir : undefined,
        q: q || undefined,
        status: status || undefined,
        category: category || undefined,
        classId: classId || undefined,
        page,
      }),
  });

  const { data: contactHits = [] } = useQuery({
    queryKey: ['opsContacts', contactQuery],
    queryFn: () => searchOpsContacts(contactQuery),
    enabled: addOpen && contactQuery.trim().length >= 2 && !contactId,
  });

  const { data: accountPrograms = [] } = useQuery({
    queryKey: ['opsAccountPrograms', cls?.accountId],
    queryFn: () => getOpsProgramsForAccount(cls!.accountId!),
    enabled: addOpen && rosterMode && Boolean(cls?.accountId),
  });

  const { data: transferHits } = useQuery({
    queryKey: ['opsClasses', transferQuery, '', '', 1],
    queryFn: () => getOpsClasses({ q: transferQuery, page: 1 }),
    enabled: Boolean(transferFor) && transferQuery.trim().length >= 2,
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['opsRegistrations'] });

  const create = useMutation({
    mutationFn: createOpsRegistrationEx,
    onSuccess: (_res, vars) => {
      showToast(
        vars.waitlist ? 'Added to the waiting list' : 'Registration added',
        'success',
      );
      setAddOpen(false);
      setContactId('');
      setContactLabel('');
      setContactQuery('');
      setNewFirst('');
      setNewLast('');
      setNewEmail('');
      setAsWaitlist(false);
      setChargeProgramId('');
      refresh();
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const transfer = useMutation({
    mutationFn: ({
      id,
      targetClassId,
    }: {
      id: string;
      targetClassId: string;
    }) => transferOpsRegistration(id, targetClassId),
    onSuccess: () => {
      showToast('Transferred', 'success');
      setTransferFor('');
      setTransferQuery('');
      refresh();
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const promote = useMutation({
    mutationFn: promoteOpsRegistration,
    onSuccess: (res) => {
      if (res.overCapacity) {
        showToast(
          `Promoted — over the contracted seat count (${res.booked}/${res.seats})`,
          'warning',
        );
      } else {
        showToast('Promoted to the roster', 'success');
      }
      refresh();
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const copyEvalLink = async (registrationId: string) => {
    try {
      const { url } = await getOpsEvalLink(registrationId);
      await navigator.clipboard.writeText(url);
      showToast('Eval link copied', 'success');
    } catch (err) {
      showToast(resolveApiMessage(err), 'error');
    }
  };

  const changeStatus = useMutation({
    mutationFn: ({ id, next }: { id: string; next: string }) =>
      setOpsRegistrationStatus(id, next),
    onSuccess: () => refresh(),
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  // In roster mode the waiting list lives in its own section below.
  const allItems = data?.items ?? [];
  const mainItems = rosterMode
    ? allItems.filter((r) => r.status !== 'Waiting List')
    : allItems;
  const waitlistItems = rosterMode
    ? allItems.filter((r) => r.status === 'Waiting List')
    : [];

  return (
    <>
      <PageHeader
        title={rosterMode ? `Roster — ${cls?.name ?? '…'}` : 'Registrations'}
        back={
          rosterMode
            ? {
                to: PATH.opsClassEdit.replace(':id', classId),
                label: 'Class',
              }
            : undefined
        }
        meta={
          rosterMode ? (
            <>
              {cls?.start?.slice(0, 10)} · {cls?.classType} ·{' '}
              {data?.total ?? '…'} on the roster
              {cls?.seats != null ? ` · ${cls.seats} seats` : ''}
              {' · '}
              <Link
                className="font-medium text-blue-primary hover:underline"
                to={PATH.opsRegistrations}
              >
                all registrations
              </Link>
            </>
          ) : (
            data && `${data.total.toLocaleString()} registrations`
          )
        }
        actions={
          rosterMode ? (
            <>
              <button
                className={btnSecondary}
                onClick={() => {
                  setBatchOpen((o) => !o);
                  setAddOpen(false);
                }}
              >
                {batchOpen ? 'Close batch' : 'Batch upload'}
              </button>
              <button
                className={addOpen ? btnSecondary : btnPrimary}
                onClick={() => {
                  setAddOpen((o) => !o);
                  setBatchOpen(false);
                }}
              >
                {addOpen ? 'Close' : 'Add person'}
              </button>
            </>
          ) : undefined
        }
      />
      {batchOpen && rosterMode && (
        <div className="px-8 pb-4">
          <BatchUploadPanel
            classId={classId}
            seriesId={cls?.series?.id ?? null}
            seriesCount={cls?.series?.count ?? null}
            onApplied={refresh}
          />
        </div>
      )}
      {!rosterMode && (
        <Toolbar>
          {urlContactId && (
            <Link
              className="rounded-full border border-blue-200 bg-blue-100/60 px-2.5 py-1 text-xs text-blue-primary"
              to={PATH.opsRegistrations}
              title="Show every registration"
            >
              {data?.items[0]?.learnerName ?? 'one person'} only ×
            </Link>
          )}
          <input
            className={`${inputClass} w-64`}
            placeholder="Search learner, email, or course…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className={inputClass}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <select
            className={inputClass}
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Public + private</option>
            <option>Public</option>
            <option>Private</option>
          </select>
          <input
            type="date"
            className={inputClass}
            title="Class start from"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
          />
          <input
            type="date"
            className={inputClass}
            title="Class start to"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
          />
          {!rosterMode && (
            <label className="flex items-center gap-1.5 text-sm text-gray-five">
              <input
                type="checkbox"
                className="ops-check"
                checked={groupByCourse}
                onChange={(e) => setGroupByCourse(e.target.checked)}
              />
              Group by course
            </label>
          )}
        </Toolbar>
      )}

      {addOpen && rosterMode && (
        <div className="px-8 pb-4">
          <Card title="Add to the roster">
            <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
              <div>
                <div className={labelClass}>Existing contact</div>
                {contactId ? (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="rounded-md bg-blue-100 px-2.5 py-1 font-medium text-blue-primary">
                      {contactLabel}
                    </span>
                    <button
                      type="button"
                      className="text-xs font-medium text-gray-bold underline hover:text-blue-primary"
                      onClick={() => {
                        setContactId('');
                        setContactLabel('');
                        setContactQuery('');
                      }}
                    >
                      change
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      className={`${inputClass} w-full`}
                      placeholder="Search 237,026 contacts by name or email…"
                      value={contactQuery}
                      onChange={(e) => setContactQuery(e.target.value)}
                    />
                    {contactHits.length > 0 && (
                      <div className="mt-1 max-h-48 overflow-y-auto rounded-md border border-white-regular bg-white shadow-light">
                        {contactHits.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            className="block w-full px-3 py-1.5 text-left text-sm transition-colors hover:bg-blue-100/40"
                            onClick={() => {
                              setContactId(c.id);
                              setContactLabel(
                                `${c.name}${c.email ? ` · ${c.email}` : ''}`,
                              );
                            }}
                          >
                            {c.name}
                            {c.email && (
                              <span className="ml-2 text-xs text-gray-bold">
                                {c.email}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div>
                <div className={labelClass}>…or a new person</div>
                <div className="flex flex-wrap gap-2">
                  <input
                    className={inputClass}
                    placeholder="First name"
                    value={newFirst}
                    onChange={(e) => setNewFirst(e.target.value)}
                    disabled={Boolean(contactId)}
                  />
                  <input
                    className={inputClass}
                    placeholder="Last name"
                    value={newLast}
                    onChange={(e) => setNewLast(e.target.value)}
                    disabled={Boolean(contactId)}
                  />
                  <input
                    className={`${inputClass} w-64`}
                    placeholder="Email (optional)"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    disabled={Boolean(contactId)}
                  />
                </div>
              </div>
              {accountPrograms.length > 0 && (
                <div>
                  <div className={labelClass}>Charge to program (optional)</div>
                  <select
                    className={`${inputClass} w-full`}
                    value={chargeProgramId}
                    onChange={(e) => setChargeProgramId(e.target.value)}
                  >
                    <option value="">— no program charge —</option>
                    {accountPrograms.map((pr) => (
                      <option key={pr.id} value={pr.id}>
                        {pr.name}
                      </option>
                    ))}
                  </select>
                  {chargeProgramId && (
                    <div className="mt-1 text-xs text-gray-bold">
                      {accountPrograms.find((pr) => pr.id === chargeProgramId)
                        ?.ledgerMode === 'live'
                        ? 'Charged to the program balance when the seat is added; a cancel reverses it.'
                        : 'Rehearsed on the program page — balances are computed by Salesforce until the portal ledger goes live.'}
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-end gap-4 md:col-span-2">
                <button
                  className={btnPrimary}
                  disabled={
                    create.isPending ||
                    (!contactId && (!newFirst.trim() || !newLast.trim()))
                  }
                  onClick={() =>
                    create.mutate({
                      classId,
                      contactId: contactId || undefined,
                      firstName: contactId ? undefined : newFirst.trim(),
                      lastName: contactId ? undefined : newLast.trim(),
                      email: contactId
                        ? undefined
                        : newEmail.trim() || undefined,
                      waitlist: asWaitlist || undefined,
                      programId: chargeProgramId || undefined,
                    })
                  }
                >
                  {create.isPending
                    ? 'Adding…'
                    : asWaitlist
                    ? 'Add to waiting list'
                    : 'Add to roster'}
                </button>
                <label className="flex items-center gap-2 pb-2 text-sm text-gray-five">
                  <input
                    type="checkbox"
                    className="ops-check h-4 w-4 accent-blue-primary"
                    checked={asWaitlist}
                    onChange={(e) => setAsWaitlist(e.target.checked)}
                  />
                  Add to waiting list
                </label>
              </div>
            </div>
          </Card>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : (
        <PageBody wide>
          <Card>
            {data && data.items.length === 0 ? (
              <EmptyState
                title={rosterMode ? 'Nobody on this roster yet' : 'No results'}
                hint={
                  rosterMode
                    ? 'Use “Add person” to put the first learner on it.'
                    : 'Try clearing the search or the filters.'
                }
              />
            ) : (
              <div className="overflow-x-auto">
                <table className={`${tableClass} min-w-[900px]`}>
                  <thead className={theadClass}>
                    <tr>
                      <SortTh
                        k="learner"
                        label="Learner"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <SortTh
                        k="email"
                        label="Email"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      {!rosterMode && (
                        <SortTh
                          k="course"
                          label="Course"
                          sort={sortKey}
                          dir={sortDir}
                          onSort={toggleSort}
                        />
                      )}
                      <SortTh
                        k="start"
                        label="Start"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      {!rosterMode && (
                        <SortTh
                          k="booked"
                          label="Booked"
                          sort={sortKey}
                          dir={sortDir}
                          onSort={toggleSort}
                        />
                      )}
                      <SortTh
                        k="category"
                        label="Cat."
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <SortTh
                        k="account"
                        label="Account"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <SortTh
                        k="status"
                        label="Status"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      {rosterMode && <th className={thClass}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(groupByCourse
                      ? [...mainItems].sort((a, b) =>
                          `${a.courseName ?? ''}${
                            a.courseStart ?? ''
                          }`.localeCompare(
                            `${b.courseName ?? ''}${b.courseStart ?? ''}`,
                          ),
                        )
                      : mainItems
                    ).map((r, idx, arr) => (
                      <React.Fragment key={r.id}>
                        {groupByCourse &&
                          (idx === 0 ||
                            arr[idx - 1].courseName !== r.courseName ||
                            arr[idx - 1].courseStart?.slice(0, 10) !==
                              r.courseStart?.slice(0, 10)) && (
                            <tr className="bg-grey-50">
                              <td
                                colSpan={9}
                                className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-bold"
                              >
                                {r.courseName ?? '(no course)'} ·{' '}
                                {r.courseStart?.slice(0, 10) ?? '—'} ·{' '}
                                {
                                  arr.filter(
                                    (x) =>
                                      x.courseName === r.courseName &&
                                      x.courseStart?.slice(0, 10) ===
                                        r.courseStart?.slice(0, 10),
                                  ).length
                                }{' '}
                                on this page
                              </td>
                            </tr>
                          )}
                        <tr>
                          <td
                            className={`${tdClass} font-medium text-gray-four`}
                          >
                            {r.contactId ? (
                              <Link
                                className="hover:text-blue-primary hover:underline"
                                to={PATH.opsContactEdit.replace(
                                  ':id',
                                  r.contactId,
                                )}
                                title="Open the person: every registration and evaluation"
                              >
                                {r.learnerName ?? '—'}
                              </Link>
                            ) : (
                              r.learnerName ?? '—'
                            )}
                            {r.localOrigin && (
                              <span className="ml-2">
                                <OriginBadge local />
                              </span>
                            )}
                          </td>
                          <td className={tdClass}>{r.email ?? '—'}</td>
                          {!rosterMode && (
                            <td className={tdClass}>
                              {r.classId ? (
                                <Link
                                  className="hover:text-blue-primary hover:underline"
                                  to={PATH.opsClassEdit.replace(
                                    ':id',
                                    r.classId,
                                  )}
                                >
                                  {r.courseName ?? '—'}
                                </Link>
                              ) : (
                                r.courseName ?? '—'
                              )}
                            </td>
                          )}
                          <td className={`${tdClass} whitespace-nowrap`}>
                            {r.courseStart ? r.courseStart.slice(0, 10) : '—'}
                          </td>
                          {!rosterMode && (
                            <td className={`${tdClass} whitespace-nowrap`}>
                              {r.bookingDate
                                ? String(r.bookingDate).slice(0, 10)
                                : '—'}
                            </td>
                          )}
                          <td className={tdClass}>{r.category ?? '—'}</td>
                          <td className={tdClass}>
                            {r.accountId ? (
                              <Link
                                className="hover:text-blue-primary hover:underline"
                                to={PATH.opsAccountView.replace(
                                  ':id',
                                  r.accountId,
                                )}
                              >
                                {r.accountName ?? '—'}
                              </Link>
                            ) : (
                              r.accountName ?? '—'
                            )}
                          </td>
                          <td className={`${tdClass} whitespace-nowrap`}>
                            <select
                              className={statusSelectClass}
                              value={r.status ?? ''}
                              onChange={(e) =>
                                changeStatus.mutate({
                                  id: r.id,
                                  next: e.target.value,
                                })
                              }
                            >
                              {STATUSES.map((s) => (
                                <option key={s}>{s}</option>
                              ))}
                            </select>
                          </td>
                          {rosterMode && (
                            <td
                              className={`${tdClass} whitespace-nowrap text-right`}
                            >
                              <button
                                className={btnGhost}
                                onClick={() => {
                                  setTransferFor(
                                    transferFor === r.id ? '' : r.id,
                                  );
                                  setTransferQuery('');
                                }}
                              >
                                Transfer…
                              </button>
                              <button
                                className={btnGhost}
                                onClick={() => copyEvalLink(r.id)}
                              >
                                Copy eval link
                              </button>
                            </td>
                          )}
                        </tr>
                        {rosterMode && transferFor === r.id && (
                          <tr>
                            <td colSpan={7} className="bg-grey-50 px-4 py-3">
                              <div className="max-w-xl">
                                <div className={labelClass}>
                                  Transfer {r.learnerName ?? 'learner'} to…
                                </div>
                                <input
                                  className={`${inputClass} w-full`}
                                  placeholder="Search classes by name…"
                                  value={transferQuery}
                                  autoFocus
                                  onChange={(e) =>
                                    setTransferQuery(e.target.value)
                                  }
                                />
                                {(transferHits?.items ?? []).length > 0 && (
                                  <div className="mt-1 max-h-48 overflow-y-auto rounded-md border border-white-regular bg-white shadow-light">
                                    {transferHits!.items.map((c) => (
                                      <button
                                        key={c.id}
                                        type="button"
                                        disabled={transfer.isPending}
                                        className="block w-full px-3 py-1.5 text-left text-sm transition-colors hover:bg-blue-100/40 disabled:opacity-50"
                                        onClick={() =>
                                          transfer.mutate({
                                            id: r.id,
                                            targetClassId: c.id,
                                          })
                                        }
                                      >
                                        {c.name}
                                        <span className="ml-2 text-xs text-gray-bold">
                                          {c.start?.slice(0, 10)} ·{' '}
                                          {c.classType}
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination
              page={page}
              pages={pages}
              total={data?.total}
              onPage={setPage}
            />
          </Card>
          {rosterMode && waitlistItems.length > 0 && (
            <Card
              title={`Waiting list · ${waitlistItems.length}`}
              className="mt-4"
            >
              <div className="overflow-x-auto">
                <table className={`${tableClass} min-w-[700px]`}>
                  <thead className={theadClass}>
                    <tr>
                      <th className={thClass}>Learner</th>
                      <th className={thClass}>Email</th>
                      <th className={thClass}>Added</th>
                      <th className={thClass}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {waitlistItems.map((r) => (
                      <tr key={r.id}>
                        <td className={`${tdClass} font-medium text-gray-four`}>
                          {r.learnerName ?? '—'}
                          {r.localOrigin && (
                            <span className="ml-2">
                              <OriginBadge local />
                            </span>
                          )}
                        </td>
                        <td className={tdClass}>{r.email ?? '—'}</td>
                        <td className={`${tdClass} whitespace-nowrap`}>
                          {r.bookingDate ?? '—'}
                        </td>
                        <td
                          className={`${tdClass} whitespace-nowrap text-right`}
                        >
                          <button
                            className={btnGhost}
                            disabled={promote.isPending}
                            onClick={() => promote.mutate(r.id)}
                          >
                            Promote
                          </button>
                          <select
                            className={statusSelectClass}
                            value={r.status ?? ''}
                            onChange={(e) =>
                              changeStatus.mutate({
                                id: r.id,
                                next: e.target.value,
                              })
                            }
                          >
                            {STATUSES.map((s) => (
                              <option key={s}>{s}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </PageBody>
      )}
    </>
  );
};

/**
 * Batch upload — the client's "add these, cancel those" list, pasted or
 * chosen as a .csv, previewed with the detected action per row, then
 * applied. Each row goes through the same add / cancel paths as a single
 * change; one refusal never stops the rest, and the outcome comes back per
 * row (and per session, for a series).
 */
const BatchUploadPanel: React.FC<{
  classId: string;
  seriesId: string | null;
  seriesCount: number | null;
  onApplied: () => void;
}> = ({ classId, seriesId, seriesCount, onApplied }) => {
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<TBatchParsedRow[] | null>(null);
  const [parsedNote, setParsedNote] = useState('');
  const [wholeSeries, setWholeSeries] = useState(false);
  const [result, setResult] = useState<TBatchResult | null>(null);

  const parse = useMutation({
    mutationFn: () => parseOpsRosterCsv(csv),
    onSuccess: (p) => {
      setRows(p.rows);
      setResult(null);
      const bits = [
        p.headerDetected
          ? 'header row detected'
          : 'no header row — columns inferred',
        `columns: ${
          p.columns.filter((c) => c !== 'ignore').join(', ') ||
          'none recognised'
        }`,
      ];
      if (p.skipped.length) {
        bits.push(
          `${p.skipped.length} ${
            p.skipped.length === 1 ? 'line' : 'lines'
          } skipped (no email)`,
        );
      }
      setParsedNote(bits.join(' · '));
      if (p.rows.length === 0) {
        showToast('No rows with an email address were found', 'error');
      }
    },
    onError: (err) => showToast(apiMessage(err), 'error'),
  });

  const apply = useMutation({
    mutationFn: () =>
      applyOpsRosterBatch({
        ...(wholeSeries && seriesId ? { seriesId } : { classId }),
        rows: (rows ?? [])
          .filter((r) => !r.problem)
          .map((r) => ({
            action: r.action,
            email: r.email,
            firstName: r.firstName ?? undefined,
            lastName: r.lastName ?? undefined,
          })),
      }),
    onSuccess: (res) => {
      setResult(res);
      const t = res.totals;
      showToast(
        `Batch applied · ${t.added} added · ${t.waitlisted} waitlisted · ${t.cancelled} cancelled · ${t.refused} refused`,
        t.refused ? 'warning' : 'success',
      );
      onApplied();
    },
    onError: (err) => showToast(apiMessage(err), 'error'),
  });

  const readFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCsv(String(reader.result ?? ''));
      setFileName(file.name);
      setRows(null);
      setResult(null);
    };
    reader.readAsText(file);
  };

  const setRowAction = (line: number, action: TBatchAction) =>
    setRows((x) =>
      (x ?? []).map((r) =>
        r.line === line
          ? {
              ...r,
              action,
              problem: r.problem?.startsWith('Unknown action')
                ? null
                : r.problem,
            }
          : r,
      ),
    );

  const ready = (rows ?? []).filter((r) => !r.problem);
  const outcomeTone = (ok: boolean, status: number) =>
    ok ? 'good' : status === 409 ? 'warn' : 'bad';

  return (
    <Card
      title="Batch upload"
      right={
        <span className="text-xs text-gray-bold">
          first name, last name, email, action (add · cancel · waitlist) — or
          just name and email
        </span>
      }
    >
      <div className="grid grid-cols-1 gap-4 p-5">
        {!result && (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="md:col-span-2">
                <label className={labelClass}>Paste the list</label>
                <textarea
                  className={`${inputClass} w-full font-mono text-xs`}
                  rows={6}
                  placeholder={
                    'first name,last name,email,action\nAda,Lovelace,ada@example.com,add\nBob,Byron,bob@example.com,cancel'
                  }
                  value={csv}
                  onChange={(e) => {
                    setCsv(e.target.value);
                    setRows(null);
                    setFileName('');
                  }}
                />
              </div>
              <div>
                <label className={labelClass}>…or choose a .csv file</label>
                <input
                  type="file"
                  accept=".csv,text/csv,text/plain,.txt,.tsv"
                  className="block w-full text-sm text-gray-five file:mr-3 file:rounded-md file:border file:border-white-two file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-gray-five"
                  onChange={(e) => readFile(e.target.files?.[0])}
                />
                {fileName && (
                  <div className="mt-1 text-xs text-gray-bold">{fileName}</div>
                )}
                <div className="mt-3 text-xs text-gray-bold">
                  Read in your browser and previewed before anything changes. An
                  action column is optional — rows without one are adds.
                  “remove” and “leave” count as cancel.
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className={btnSecondary}
                disabled={parse.isPending || !csv.trim()}
                onClick={() => parse.mutate()}
              >
                {parse.isPending ? 'Reading…' : 'Preview'}
              </button>
              {seriesId && (seriesCount ?? 0) > 1 && (
                <label className="flex items-center gap-2 text-sm text-gray-five">
                  <input
                    type="checkbox"
                    className="ops-check h-4 w-4 accent-blue-primary"
                    checked={wholeSeries}
                    onChange={(e) => setWholeSeries(e.target.checked)}
                  />
                  Apply to every session of the series ({seriesCount})
                </label>
              )}
              {parsedNote && (
                <span className="text-xs text-gray-bold">{parsedNote}</span>
              )}
            </div>
          </>
        )}

        {rows && !result && rows.length > 0 && (
          <>
            <div className="overflow-x-auto rounded-lg border border-white-three">
              <table className={tableClass}>
                <thead className={theadClass}>
                  <tr>
                    <th className={thClass}>Line</th>
                    <th className={thClass}>Name</th>
                    <th className={thClass}>Email</th>
                    <th className={thClass}>Action</th>
                    <th className={thClass}>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.line} className={r.problem ? 'opacity-70' : ''}>
                      <td className={`${tdClass} text-gray-bold`}>{r.line}</td>
                      <td className={tdClass}>
                        {[r.firstName, r.lastName]
                          .filter(Boolean)
                          .join(' ') || (
                          <span className="text-gray-two">
                            {r.action === 'cancel' ? '—' : 'no name'}
                          </span>
                        )}
                      </td>
                      <td className={tdClass}>{r.email}</td>
                      <td className={tdClass}>
                        <select
                          className={statusSelectClass}
                          value={r.action}
                          onChange={(e) =>
                            setRowAction(r.line, e.target.value as TBatchAction)
                          }
                        >
                          {BATCH_ACTIONS.map((a) => (
                            <option key={a} value={a}>
                              {a}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className={`${tdClass} text-xs`}>
                        {r.problem ? (
                          <span className="text-red-one">
                            {r.problem} — skipped
                          </span>
                        ) : r.action !== 'cancel' &&
                          (!r.firstName || !r.lastName) ? (
                          <span className="text-gray-bold">
                            Needs an existing contact with this email, or both
                            names
                          </span>
                        ) : (
                          <span className="text-gray-two">ready</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className={btnPrimary}
                disabled={apply.isPending || ready.length === 0}
                onClick={() => apply.mutate()}
              >
                {apply.isPending
                  ? 'Applying…'
                  : `Apply ${ready.length} ${
                      ready.length === 1 ? 'row' : 'rows'
                    }${wholeSeries && seriesId ? ' to the series' : ''}`}
              </button>
              <span className="text-xs text-gray-bold">
                Adds and waitlists use the class’s seat count and duplicate
                checks; a refused row is reported, the rest still go through.
              </span>
            </div>
          </>
        )}

        {result && (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Pill value={`${result.totals.added} added`} tone="good" />
              <Pill
                value={`${result.totals.waitlisted} waitlisted`}
                tone="info"
              />
              <Pill
                value={`${result.totals.cancelled} cancelled`}
                tone="neutral"
              />
              <Pill
                value={`${result.totals.refused} refused`}
                tone={result.totals.refused ? 'warn' : 'neutral'}
              />
              <span className="text-xs text-gray-bold">
                across {result.classes.length}{' '}
                {result.classes.length === 1 ? 'class' : 'sessions'}
              </span>
            </div>
            <div className="overflow-x-auto rounded-lg border border-white-three">
              <table className={tableClass}>
                <thead className={theadClass}>
                  <tr>
                    <th className={thClass}>Email</th>
                    <th className={thClass}>Action</th>
                    {result.classes.length > 1 && (
                      <th className={thClass}>Session</th>
                    )}
                    <th className={thClass}>Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.flatMap((r) =>
                    r.outcomes.map((o) => (
                      <tr key={`${r.index}-${o.classId}`}>
                        <td className={tdClass}>
                          {r.name ? (
                            <>
                              <span className="font-medium text-gray-four">
                                {r.name}
                              </span>{' '}
                              <span className="text-gray-bold">{r.email}</span>
                            </>
                          ) : (
                            r.email
                          )}
                        </td>
                        <td className={tdClass}>{r.action}</td>
                        {result.classes.length > 1 && (
                          <td className={`${tdClass} text-xs`}>
                            {o.className ?? o.classId}
                          </td>
                        )}
                        <td className={tdClass}>
                          <Pill
                            value={
                              o.ok
                                ? r.action === 'cancel'
                                  ? 'Cancelled'
                                  : r.action === 'waitlist'
                                  ? 'Waitlisted'
                                  : 'Added'
                                : `${o.status} · ${o.message ?? 'refused'}`
                            }
                            tone={outcomeTone(o.ok, o.status)}
                          />
                        </td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            </div>
            <div>
              <button
                type="button"
                className={btnSecondary}
                onClick={() => {
                  setResult(null);
                  setRows(null);
                  setCsv('');
                  setFileName('');
                  setParsedNote('');
                }}
              >
                Start another batch
              </button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

export default OpsRegistrations;
